import React from 'react';
import { createPortal } from 'react-dom';
import { X, Image as ImageIcon, PenLine } from 'lucide-react';
import { getImageUrl } from '../api';

const TERMS_TEXT = `I, {name} , working at {center} Center, hereby agree to the following terms and conditions set by the Company. I understand that my salary will be based on student admissions, assigned students, student learning performance, and course completion as per Company policy. I agree to strictly follow all Company software systems, operational guidelines, and management instructions.

and future assignments.

1. HRMSSECURE (Attendance, Task & Leave Management System)

HRMSSECURE is the official Attendance, Task, and Leave Management System of the Company. The Employee agrees to strictly comply with the following:

1.1 Attendance Rules

1. Attendance must be marked strictly according to assigned Shift Start Time and Shift End Time.
2. A buffer of 10 minutes is allowed after shift start time.
3. Late arrivals beyond the buffer period will be considered as Late Mark.
4. Unauthorized absences will result in salary deductions.

1.2 Task Completion

1. All assigned tasks must be completed within the given deadline.
2. Daily Work Reports (DWR) must be submitted every day before 8:00 PM.
3. Incomplete tasks and missed DWRs will impact performance evaluation.

1.3 Leave Policy

1. Leave requests must be submitted in advance through the system.
2. Unapproved leaves will be treated as Loss of Pay (LOP).
3. Employees are eligible for leaves as per Company policy only.`;

export default function AgreementDetailsModal({ isOpen, onClose, agreement }) {
  if (!isOpen || !agreement) return null;

  const termsText = TERMS_TEXT.replace(/{name}/g, agreement.employee).replace(/{center}/g, agreement.center);

  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
      
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-[800px] flex flex-col max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
          <h2 className="text-lg font-black text-slate-800">Agreement Details</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar bg-white">

          {/* Top Info Strip */}
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block mb-1">EMPLOYEE</span>
                <span className="text-[13px] font-black text-slate-800">{agreement.employee_name}</span>
              </div>
              <div>
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block mb-1">CENTER</span>
                <span className="text-[13px] font-black text-slate-800">{agreement.center_name}</span>
              </div>
              <div>
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block mb-1">SIGNED ON</span>
                <span className="text-[13px] font-black text-slate-800">{new Date(agreement.agreed_at || agreement.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Photo & Signature */}
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="grid grid-cols-2 gap-6">
              {/* Live Photo */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <ImageIcon size={14} className="text-emerald-500" />
                  <span className="text-[11px] font-black text-slate-600 uppercase tracking-wider">Live Photo Verification</span>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg h-[240px] overflow-hidden flex items-center justify-center">
                  {agreement.photo_path ? (
                    <img 
                      src={getImageUrl(agreement.photo_path)}
                      alt="Live Verification"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="text-slate-400 text-[11px] font-bold">No Photo Available</div>
                  )}
                </div>
              </div>

              {/* Digital Signature */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <PenLine size={14} className="text-indigo-500" />
                  <span className="text-[11px] font-black text-slate-600 uppercase tracking-wider">Digital Signature</span>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg h-[240px] flex items-center justify-center overflow-hidden">
                  {agreement.signature_path ? (
                    <img 
                      src={getImageUrl(agreement.signature_path)}
                      alt="Digital Signature"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="text-slate-400 text-[11px] font-bold">No Signature Available</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Agreed Terms */}
          <div className="px-6 py-5">
            <h3 className="text-[13px] font-black text-slate-700 mb-3">Agreed Terms Snapshot</h3>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 h-[200px] overflow-y-auto custom-scrollbar text-[12px] text-slate-600 font-medium leading-relaxed whitespace-pre-line">
              <p>
                I, <span className="text-indigo-600 font-bold underline">{agreement.employee}</span> , working at <span className="text-indigo-600 font-bold underline">{agreement.center}</span> Center, hereby agree to the following terms and conditions set by the Company. I understand that my salary will be based on student admissions, assigned students, student learning performance, and course completion as per Company policy. I agree to strictly follow all Company software systems, operational guidelines, and management instructions.
              </p>
              <p className="mt-3">and future assignments.</p>
              <p className="font-black text-slate-800 mt-3">1. HRMSSECURE (Attendance, Task & Leave Management System)</p>
              <p className="mt-1">HRMSSECURE is the official Attendance, Task, and Leave Management System of the Company. The Employee agrees to strictly comply with the following:</p>
              <p className="font-black text-slate-800 mt-3">1.1 Attendance Rules</p>
              <p className="mt-1">1. Attendance must be marked strictly according to assigned Shift Start Time and Shift End Time.
2. A buffer of 10 minutes is allowed after shift start time.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-white flex justify-end">
          <button 
            onClick={onClose}
            className="px-6 py-2.5 font-bold text-[13px] text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
          >
            Close Details
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
